import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Send, Sparkles, Bot, Phone, Check, CheckCheck, Radio, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const QUICK_REPLIES = [
  "ajuda",
  "status",
  "temperatura camara 1 4 graus",
  "ocorrencia: vazamento na cozinha",
];

function DeliveryTick({ msg }) {
  if (msg.direction !== "in") return null;
  const s = msg.delivery_status;
  if (s === "read")
    return <CheckCheck className="h-3 w-3 text-sky-600" title="Lido" />;
  if (s === "delivered")
    return <CheckCheck className="h-3 w-3 text-emerald-700" title="Entregue" />;
  if (s === "failed" || s === "undelivered")
    return <AlertCircle className="h-3 w-3 text-rose-600" title={msg.delivery_error || "Falha"} />;
  if (s === "sent" || s === "queued")
    return <Check className="h-3 w-3 text-stone-500" title="Enviado" />;
  // No status yet (UI MOCK / not via Twilio): show single check as before
  return <Check className="h-3 w-3 text-emerald-700" />;
}

function MessageBubble({ msg }) {
  const isIn = msg.direction === "in";
  const time = msg.created_at
    ? new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : "";
  return (
    <div className={`flex ${isIn ? "justify-end" : "justify-start"} mb-2`}>
      <div
        className={[
          "max-w-[78%] px-3 py-2 rounded-2xl font-body text-sm relative shadow-sm",
          isIn
            ? "bg-emerald-100 text-stone-900 rounded-br-md"
            : "bg-white text-stone-900 rounded-bl-md border border-stone-200",
        ].join(" ")}
      >
        <div className="whitespace-pre-wrap break-words">{msg.text}</div>
        <div className="text-[10px] text-stone-500 mt-1 text-right flex items-center justify-end gap-1">
          {time}
          {isIn && <DeliveryTick msg={msg} />}
        </div>
        {!isIn && msg.intent && msg.intent !== "unknown" && (
          <Badge className="bg-emerald-700 text-white text-[9px] absolute -top-2 -left-1 border-none">
            {msg.intent}
          </Badge>
        )}
      </div>
    </div>
  );
}

export default function Chatbot() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [phone, setPhone] = useState("+5511999900001");
  const [sending, setSending] = useState(false);
  const [twStatus, setTwStatus] = useState(null);
  const [realSending, setRealSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const [msgs, status] = await Promise.all([
        api.get("/chatbot/messages"),
        api.get("/chatbot/status"),
      ]);
      setMessages(msgs.data);
      setTwStatus(status.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Poll periodically so delivery status updates propagate without a refresh
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/chatbot/messages");
        setMessages(data);
      } catch (err) {
        // silent
      }
    }, 12000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (override) => {
    const body = (override ?? text).trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post("/chatbot/messages", {
        phone_number: phone,
        text: body,
      });
      setMessages((m) => [...m, data.inbound, data.outbound]);
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no envio");
    } finally {
      setSending(false);
    }
  };

  const sendReal = async () => {
    const body = text.trim();
    if (!body) return;
    setRealSending(true);
    try {
      const { data } = await api.post("/chatbot/send", {
        phone_number: phone,
        text: body,
      });
      setMessages((m) => [...m, data]);
      setText("");
      toast.success("Mensagem real enviada via Twilio");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar via Twilio");
    } finally {
      setRealSending(false);
    }
  };

  return (
    <Layout
      title="Chatbot WhatsApp"
      subtitle={
        twStatus?.twilio_configured
          ? "Simulador + envio real via Twilio com IA"
          : "Simulador de mensagens com IA (MOCK — sem Twilio)"
      }
      actions={
        <div className="flex items-center gap-2">
          {twStatus?.twilio_configured ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-none">
              <Radio className="h-3 w-3 mr-1" /> Twilio ao vivo
            </Badge>
          ) : (
            <Badge variant="outline" className="border-amber-300 text-amber-800">
              Twilio desconfigurado · modo MOCK
            </Badge>
          )}
          <Badge variant="outline" className="border-emerald-200 text-emerald-800">
            <Sparkles className="h-3 w-3 mr-1" /> Claude Sonnet
          </Badge>
        </div>
      }
    >
      <div data-testid="chatbot-page" className="grid lg:grid-cols-3 gap-5 h-[calc(100vh-180px)]">
        {/* Conversation */}
        <Card className="lg:col-span-2 flex flex-col rounded-xl border-stone-200 bg-stone-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 bg-gradient-to-r from-emerald-700 to-emerald-800 text-white flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight">Ultra Bot</div>
              <div className="text-[11px] text-emerald-100 flex items-center gap-1">
                <Phone className="h-3 w-3" /> {phone}
              </div>
            </div>
          </div>

          {/* Messages canvas with chat texture */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4 bg-[length:30px_30px]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)",
            }}
            data-testid="chatbot-messages"
          >
            {messages.length === 0 && (
              <div className="text-center text-stone-500 font-body py-16">
                <MessageCircle className="h-8 w-8 mx-auto mb-3 text-stone-400" />
                Sem mensagens ainda. Use &quot;ajuda&quot; para começar.
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-stone-200 bg-white p-3">
            <div className="flex flex-wrap gap-1 mb-2">
              {QUICK_REPLIES.map((q) => (
                <Button
                  key={q}
                  size="sm"
                  variant="outline"
                  className="text-xs border-stone-200 bg-stone-50 hover:bg-emerald-50"
                  onClick={() => send(q)}
                  disabled={sending}
                  data-testid={`chatbot-quick-${q.split(" ")[0]}`}
                >
                  {q.length > 32 ? q.slice(0, 32) + "…" : q}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Digite uma mensagem…"
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                className="bg-stone-50"
                data-testid="chatbot-input"
                autoFocus
              />
              <Button
                onClick={() => send()}
                disabled={sending || !text.trim()}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="chatbot-send"
                title="Enviar como simulação (UI mock)"
              >
                <Send className="h-4 w-4" />
              </Button>
              {twStatus?.twilio_configured && (
                <Button
                  onClick={sendReal}
                  disabled={realSending || !text.trim()}
                  variant="outline"
                  className="border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                  data-testid="chatbot-send-real"
                  title="Enviar como WhatsApp real via Twilio"
                >
                  {realSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Radio className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Side panel */}
        <div className="space-y-4">
          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <h3 className="font-display font-bold tracking-tight">Simular outro número</h3>
            <p className="text-xs text-stone-500 font-body mt-1">
              Cada número simula um operador diferente conversando com o bot.
            </p>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-3 bg-white"
              placeholder="+5511999900001"
              data-testid="chatbot-phone"
            />
          </Card>

          <Card className="p-5 rounded-xl border-stone-200 bg-stone-950 text-stone-100">
            <h3 className="font-display font-bold tracking-tight text-emerald-300">
              Comandos suportados
            </h3>
            <ul className="text-xs font-body mt-3 space-y-2 text-stone-300">
              <li>
                <b className="text-emerald-400">ajuda</b> — lista comandos
              </li>
              <li>
                <b className="text-emerald-400">status</b> — checklists pendentes
              </li>
              <li>
                <b className="text-emerald-400">temperatura camara 1 5 graus</b> — leitura livre via IA
              </li>
              <li>
                <b className="text-emerald-400">ocorrencia: …</b> — abre ocorrência
              </li>
            </ul>
          </Card>

          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <h3 className="font-display font-bold tracking-tight">
              {twStatus?.twilio_configured ? "Twilio conectado" : "Integração WhatsApp"}
            </h3>
            <p className="text-xs text-stone-500 font-body mt-2 leading-relaxed">
              {twStatus?.twilio_configured ? (
                <>
                  Twilio ativo. Envios reais usam o número{" "}
                  <code className="px-1 py-0.5 bg-stone-100 rounded text-stone-800 text-[10px]">
                    {twStatus.sender}
                  </code>
                  . Para receber mensagens, aponte o webhook do Twilio para{" "}
                  <code className="px-1 py-0.5 bg-stone-100 rounded text-stone-800 text-[10px]">
                    /api/chatbot/twilio/webhook
                  </code>
                  .
                </>
              ) : (
                <>
                  Este simulador chama exatamente o mesmo backend que receberá o webhook do
                  Twilio. Para ativar envio real, configure as variáveis{" "}
                  <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code> e{" "}
                  <code>TWILIO_WHATSAPP_FROM</code>.
                </>
              )}
            </p>
            {twStatus?.twilio_configured && <TemplateStatus template={twStatus.template} />}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

const TEMPLATE_LABELS = {
  approved: { label: "Template aprovado pela Meta", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Template em análise na Meta", cls: "bg-amber-100 text-amber-800" },
  received: { label: "Template em análise na Meta", cls: "bg-amber-100 text-amber-800" },
  rejected: { label: "Template rejeitado pela Meta", cls: "bg-red-100 text-red-800" },
};

function TemplateStatus({ template }) {
  if (!template?.configured) {
    return (
      <p data-testid="whatsapp-template-status" className="text-xs text-amber-800 font-body mt-3">
        Sem template aprovado: mensagens proativas só chegam dentro da janela de 24h.
      </p>
    );
  }
  const meta = TEMPLATE_LABELS[template.status] || {
    label: `Template: ${template.status || "desconhecido"}`,
    cls: "bg-stone-100 text-stone-700",
  };
  return (
    <div data-testid="whatsapp-template-status" className="mt-3 space-y-1.5">
      <Badge className={`${meta.cls} border-none`}>{meta.label}</Badge>
      <p className="text-[11px] text-stone-500 font-body leading-relaxed">
        Fora da janela de 24h, notificações usam o template{" "}
        <code className="px-1 py-0.5 bg-stone-100 rounded text-stone-800 text-[10px]">
          {template.name}
        </code>
        {template.rejection_reason ? ` · Motivo: ${template.rejection_reason}` : ""}
      </p>
    </div>
  );
}
